/**
 * Whichever reservation dialog is currently open, plus the acknowledge-this
 * modal an outcome may leave behind (BON-472).
 *
 * One place rather than three blocks at the bottom of the panel, and the reason
 * it is worth a component: the ALERT IS RENDERED ALONGSIDE, not instead of, so
 * news cannot be lost by the dialog closing underneath it. The request already
 * committed — closing the form does not undo it.
 *
 * The edit form is the exception, and deliberately so: its failures never reach
 * this alert at all. They stay inline in the open form, because the address the
 * CSR just typed is the one thing on screen that reloading cannot recover.
 */
import { ErrorDialog } from '../../shared/ErrorDialog';

import { EditReservationContactDialog } from './EditReservationContactDialog';
import { ReleaseReservationDialog } from './ReleaseReservationDialog';
import { ResendReservationDialog } from './ResendReservationDialog';
import type { ReservationRowActionsState } from './useReservationRowActions';

export function ReservationActionDialogs({
  actions,
  actorName,
}: {
  actions: ReservationRowActionsState;
  /** Shown in the release dialog so the CSR sees whose name goes on the audit
   *  entry. */
  actorName: string;
}) {
  const { active, alert, close, dismissAlert, settle, refreshList } = actions;

  return (
    <>
      {active?.kind === 'resend' && (
        <ResendReservationDialog reservation={active.row} onClose={close} onDone={settle} />
      )}
      {active?.kind === 'edit' && (
        <EditReservationContactDialog
          reservation={active.row}
          onClose={close}
          onDone={settle}
          onRefresh={refreshList}
        />
      )}
      {active?.kind === 'release' && (
        <ReleaseReservationDialog
          reservation={active.row}
          actorName={actorName}
          onClose={close}
          // Release reports its own refusals inline and has no email to fail, so
          // it needs nothing said about it — just the row re-read.
          onReleased={() => settle({ refresh: true })}
        />
      )}
      {alert && (
        <ErrorDialog
          title={alert.title}
          message={alert.message}
          tone={alert.tone}
          onClose={dismissAlert}
        />
      )}
    </>
  );
}
